import { useCallback } from 'react'
import {
  ControlMessage,
  deferInAppMessage,
  InAppMessage,
  showInAppMessage,
  subscribeToInAppMessage,
} from '@braze/web-sdk'
import { StringValues } from './types'
import { useFeatureFlag } from '@/lib/use-feature-flag'
import { RegisteredRouter } from '@tanstack/react-router'

export const useBrazeInAppMessagesSubscription = <
  Extras extends StringValues<Extras>,
>({
  router,
}: {
  router: RegisteredRouter
}) => {
  const { enabled: isIppEnabled } = useFeatureFlag({
    flagKey: 'eclipse-release-in-product-popups',
  })

  const handleInAppMessages = useCallback(
    (inAppMessage: InAppMessage | ControlMessage) => {
      const isOnHomePage = !!router.matchRoute({ to: '/' })

      if (inAppMessage.isControl) {
        return showInAppMessage(inAppMessage)
      }

      if (isOnHomePage) {
        showInAppMessage(inAppMessage)
      } else {
        deferInAppMessage(inAppMessage)
      }
    },
    [router],
  )

  const subscribeToInAppMessages = useCallback(() => {
    let inAppMessageSubGuid: string | undefined

    if (isIppEnabled) {
      try {
        inAppMessageSubGuid = subscribeToInAppMessage(handleInAppMessages)
      } catch (e) {
        console.error(
          'Braze: Failed to subscribe to in-app messages. Continuing setup...',
          e,
        )
      }
    }

    return inAppMessageSubGuid
  }, [handleInAppMessages, isIppEnabled])

  return {
    subscribeToInAppMessages,
    handleInAppMessages,
  }
}
